/**
 * QUIPWA — Translation catalog.
 *
 * `es` is the reference locale: it defines the key set, and every other locale
 * is typed against it, so the compiler flags a missing or stray key instead of
 * letting it surface as a raw identifier in the UI.
 *
 * Keys are namespaced by area (`notes.*`, `settings.*`) and named for meaning,
 * not for the Spanish wording — renaming copy shouldn't mean renaming keys.
 *
 * Placeholders use `{name}` and are filled by `t(key, { name: value })`.
 */

import { SYSTEM_EN, SYSTEM_ES } from './catalog-system.ts';
import { SETTINGS_EN, SETTINGS_ES } from './catalog-settings.ts';
import { APPEARANCE_EN, APPEARANCE_ES } from './catalog-appearance.ts';
import { STARTER_EN, STARTER_ES } from './catalog-starter.ts';
import { DECK_EDITOR_EN, DECK_EDITOR_ES } from './catalog-deck-editor.ts';
import { SEARCH_EN, SEARCH_ES } from './catalog-search.ts';
import { EXAM_EN, EXAM_ES } from './catalog-exam.ts';
import { VOICE_EN, VOICE_ES } from './catalog-voice.ts';
import { INTEGRITY_EN, INTEGRITY_ES } from './catalog-integrity.ts';

export const LOCALES = ['es', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Shown in the language picker — always in the language itself, never translated. */
export const LOCALE_LABELS: Record<Locale, string> = {
  es: 'Español',
  en: 'English',
};

const es = {
  // ── Shared domain vocabulary ────────────────────────────────────────────
  // Note types and statuses are stored in frontmatter in English ('Book',
  // 'Pending', …). These map the stored value to what the user reads, so the
  // files stay portable no matter which language the UI is in.
  'noteType.Quick Note': 'Nota rápida',
  'noteType.Book': 'Libro',
  'noteType.Short Course': 'Curso corto',
  'noteType.Long Course': 'Curso largo',

  'noteStatus.Pending': 'Pendiente',
  'noteStatus.In Progress': 'En progreso',
  'noteStatus.Completed': 'Completado',
  'noteStatus.Mastered': 'Dominado',

  // ── Common ──────────────────────────────────────────────────────────────
  'common.all': 'Todos',
  'common.none': '—',
  'common.clear': 'Limpiar',
  'common.cancel': 'Cancelar',
  'common.close': 'Cerrar',
  'common.sure': '¿Seguro?',
  'key.space': 'Espacio',
  'key.escape': 'Esc',

  // ── Notes directory ─────────────────────────────────────────────────────
  'notes.title': 'Base de conocimientos',
  'notes.count': '{count} nota(s)',
  'notes.searchPlaceholder': 'Buscar notas, dominios, etiquetas…',
  'notes.filters': 'Filtros',
  'notes.import': 'Importar',
  'notes.newNote': 'Nueva nota',

  'notes.upgradeToLongCourse': 'Convertir en Curso largo',
  'notes.promoting': 'convirtiendo…',

  'notes.col.title': 'Título',
  'notes.col.type': 'Tipo',
  'notes.col.status': 'Estado',
  'notes.col.domain': 'Dominio',
  'notes.col.tags': 'Etiquetas',
  'notes.col.updated': 'Actualizada',

  'notes.filter.type': 'Tipo',
  'notes.filter.status': 'Estado',
  'notes.filter.domain': 'Dominio',
  'notes.filter.clearAll': 'Limpiar filtros',
  'notes.filter.noDomains': 'Sin dominios todavía',

  'notes.empty': 'Ninguna nota coincide con la búsqueda.',
  'notes.emptyFiltered': 'Ninguna nota coincide con los filtros.',
  'notes.delete': 'Eliminar nota',
  'notes.deleteAria': 'Eliminar {title}',

  // Selection + deletion
  'notes.select': 'Seleccionar {title}',
  'notes.selectAll': 'Seleccionar todo',
  'notes.selectedCount': '{count} seleccionada(s)',
  'notes.clearSelection': 'Quitar selección',
  'notes.deleteSelected': 'Eliminar',

  'notes.deleteModal.titleOne': '¿Eliminar esta nota?',
  'notes.deleteModal.titleMany': '¿Eliminar {count} notas?',
  'notes.deleteModal.warning': 'Se eliminan de Google Drive. No se puede deshacer.',
  'notes.deleteModal.courseWarning':
    'Incluye {count} curso(s) largo(s): también se eliminarán sus módulos ({modules} en total).',
  'notes.deleteModal.andMore': '…y {count} más',
  'notes.deleteModal.confirm': 'Sí, eliminar',
  'notes.deleteModal.deleting': 'Eliminando…',
  'notes.deleteModal.failed': 'No se pudo eliminar {count} nota(s). Siguen en Drive.',

  'notes.footer.count': '{shown} de {total} notas',
  'notes.footer.sortedBy': 'Ordenado por {field}',

  'notes.time.justNow': 'Ahora mismo',
  'notes.time.hoursAgo': 'hace {n} h',
  'notes.time.daysAgo': 'hace {n} d',

  // ── Create-note modal ───────────────────────────────────────────────────
  'createNote.title': 'Nueva nota',
  'createNote.subtitle': 'Selecciona el tipo para inyectar la plantilla adecuada',
  'createNote.titlePlaceholder': 'Título de la nota…',
  'createNote.domainPlaceholder': 'ej. Matemáticas',
  'createNote.tagsPlaceholder': 'Separados por coma',
  'createNote.template': 'Plantilla',
  'createNote.status': 'Estado',
  'createNote.domain': 'Dominio',
  'createNote.tags': 'Etiquetas',
  'createNote.creating': 'Creando…',
  'createNote.create': 'Crear nota',
  'noteType.Quick Note.desc': 'Captura libre, sin estructura',
  'noteType.Book.desc': 'Lectura con notas y citas',
  'noteType.Short Course.desc': 'Aprendizaje estructurado',
  'noteType.Long Course.desc': 'Múltiples módulos en profundidad',

  // ── Import-notes modal ──────────────────────────────────────────────────
  'importNotes.title': 'Importar notas',
  'importNotes.hint': 'Archivos .md sueltos o una carpeta entera (vault)',
  'importNotes.pickFiles': 'Elegir archivos',
  'importNotes.pickFilesHint': 'Uno o varios .md',
  'importNotes.pickFolder': 'Elegir carpeta',
  'importNotes.pickFolderHint': 'Importa todos los .md que contenga',
  'importNotes.unreadable': 'No se pudo leer',
  'importNotes.imported': '{count} notas importadas',
  'importNotes.failed': '{count} fallaron:',
  'importNotes.importing': 'Importando… {progress}%',
  'importNotes.submit': 'Importar {count}',
  'importNotes.summary': '{count} notas listas para importar',

  // ── Tag detail ──────────────────────────────────────────────────────────
  'tagDetail.loading': 'Cargando mazos…',
  'tagDetail.notFound': 'Etiqueta no encontrada',
  'tagDetail.goToLibrary': 'Ir a la biblioteca',
  'tagDetail.empty': 'No hay mazos ni notas con esta etiqueta todavía.',
  'tagDetail.edit': 'Editar etiqueta',
  'tagDetail.name': 'Nombre',
  'tagDetail.namePlaceholder': 'ej. biología',
  'tagDetail.color': 'Color',
  'tagDetail.save': 'Guardar cambios',
  'tagDetail.saving': 'Guardando…',
  'tagDetail.confirm': 'Confirmar',

  // ── Sync conflicts ──────────────────────────────────────────────────────
  'conflict.bannerOne': '{count} cambio se resolvió automáticamente al sincronizar',
  'conflict.bannerMany': '{count} cambios se resolvieron automáticamente al sincronizar',
  'conflict.review': 'Revisar',
  'conflict.title': 'Cambios en conflicto',
  'conflict.subtitle': 'Se editó lo mismo en dos dispositivos. Ya se aplicó una versión — elige si prefieres la otra.',
  'conflict.thisDevice': 'Este dispositivo',
  'conflict.otherDevice': 'Drive (otro dispositivo)',
  'conflict.applied': 'Aplicada',
  'conflict.keepThis': 'Mantener esta',
  'conflict.useThis': 'Usar esta',
  'conflict.noData': 'Sin datos',
  'conflict.note': 'Nota',
  'conflict.reviewProgress': 'Progreso de repaso',
  'conflict.detected': 'Detectado {when}',
  'conflict.unavailable': 'Esa versión no está disponible',
  'conflict.dbNotReady': 'La base de datos no está lista',
  'conflict.notesDbNotReady': 'La base de datos de notas no está lista',
  'conflict.applyFailed': 'No se pudo aplicar la versión',
  'conflict.characters': '{count} caracteres',
  'conflict.reviewsAndLapses': '{reps} repasos · {lapses} fallos',
  'conflict.neverReviewed': 'sin repasos',

  'time.justNow': 'hace un momento',
  'time.minutesAgo': 'hace {n} min',
  'time.hoursAgo': 'hace {n} h',
  'time.daysAgo': 'hace {n} d',

  // ── Command palette ─────────────────────────────────────────────────────
  'palette.placeholder': 'Busca un mazo o comando…',
  'palette.noResults': 'No se encontraron resultados',
  'palette.enterToSelect': 'ENTER para seleccionar',
  'palette.toNavigate': 'para navegar',
  'palette.navigation': 'Navegación',
  'palette.goHome': 'Ir a Inicio',
  'palette.toggleLight': 'Alternar modo claro',
  'palette.toggleSkin': 'Cambiar de piel (Plana / Fichero)',
  'palette.soundOn': 'Activar sonido de Fichero',
  'palette.soundOff': 'Desactivar sonido de Fichero',
  'palette.goNotes': 'Ir a Notas',
  'palette.importDeck': 'Importar nuevo mazo',
  'palette.accountSettings': 'Ajustes de cuenta',

  // ── Deck import (.csv / .wflash) ────────────────────────────────────────
  'importDeck.title': 'Importar mazo',
  'importDeck.drop': 'Arrastra tu archivo aquí',
  'importDeck.formats': 'CSV · .wflash',
  'importDeck.badFormat': 'Formato no soportado. Usa .csv o .wflash',
  'importDeck.noCards': 'No se encontraron tarjetas válidas',
  'importDeck.deckName': 'Nombre del mazo',
  'importDeck.deckNamePlaceholder': 'ej. Arquitectura de Computadoras',
  'importDeck.newDeck': 'Nuevo mazo',
  'importDeck.preview': 'Vista previa',
  'importDeck.sample': 'Muestra',
  'importDeck.pressEnter': 'Presiona Enter o el botón para continuar',
  'importDeck.confirm': 'Confirmar importación',
  'importDeck.importing': 'Importando…',
  'importDeck.done': 'Listo',
  'importDeck.ready': '¡Listo!',
  'importDeck.importAnother': 'Importar otro',

  // ── Deck detail ─────────────────────────────────────────────────────────
  'deckDetail.notFound': 'Mazo no encontrado',
  'deckDetail.library': 'Biblioteca',
  'deckDetail.upcoming': 'Próximos eventos',
  'deckDetail.noTasks': 'Sin tareas programadas en Google Tasks',
  'deckDetail.anatomy': 'Anatomía del mazo',
  'deckDetail.retention': 'Retención',
  'deckDetail.visualCustom': 'Personalización visual',
  'deckDetail.accentColor': 'Color de acento',
  'deckDetail.bgTexture': 'Textura de fondo',
  'deckDetail.computingStats': 'Calculando estadísticas…',
  'deckDetail.history': 'Historial de evolución',
  'deckDetail.recentSessions': 'Sesiones recientes',
  'deckDetail.noSessions': 'Sin sesiones registradas todavía',
  'deckDetail.today': 'Hoy',
  'deckDetail.oldest': 'Más antiguo',
  'texture.minimal': 'Minimal (sin textura)',
  'texture.dotted': 'Puntos (dotted grid)',
  'texture.ruled': 'Líneas (ruled paper)',
  'texture.grid': 'Cuadrícula (grid)',

  // ── Deck editor ─────────────────────────────────────────────────────────
  'cardEditor.selectCard': 'Selecciona una tarjeta para editar',
  'cardEditor.front': 'Pregunta / Frente',
  'cardEditor.back': 'Respuesta / Reverso',
  'cardEditor.frontPlaceholder': 'Escribe la pregunta…',
  'cardEditor.backPlaceholder': 'Escribe la respuesta. Puedes usar $LaTeX$ para fórmulas.',
  'cardEditor.search': 'Buscar…',
  'cardEditor.noResults': 'Sin resultados',
  'cardEditor.preview': 'Vista previa',
  'cardEditor.question': 'Pregunta',
  'cardEditor.answer': 'Respuesta',

  // ── Note editor chrome ──────────────────────────────────────────────────
  'noteEditor.loading': 'Cargando nota',
  'noteEditor.downloading': 'Descargando desde Google Drive…',
  'noteEditor.loadError': 'Error al cargar',
  'noteEditor.loadingImage': 'Cargando imagen…',
  'noteEditor.loadingNote': 'Cargando…',
  'noteEditor.noteLoadFailed': 'No se pudo cargar la nota.',
  'noteEditor.mathPlaceholder': 'LaTeX — ej. E = mc^2',
  'noteEditor.mathHint': 'Enter para guardar · Esc para cancelar',
  'noteEditor.noNotesFound': 'No se encontraron notas',
  'noteEditor.noResult': 'Sin resultados',
  'noteEditor.togglePlaceholder': 'Desplegable',

  // ── Flashcard from note ─────────────────────────────────────────────────
  'flashcard.title': 'Crear flashcard',
  'flashcard.targetDeck': 'Mazo destino',
  'flashcard.deckPlaceholder': 'ej. Álgebra — Cards',
  'flashcard.front': 'Pregunta (frente)',
  'flashcard.back': 'Respuesta (reverso)',
  'flashcard.backPlaceholder': 'Escribe la respuesta…',

  // ── Reading / editing chrome ────────────────────────────────────────────
  'read.find': 'Buscar en la nota…',
  'read.replace': 'Reemplazar con…',
  'read.outline': 'Esquema',
  'read.lineHeight': 'Interlineado',
  'read.font': 'Fuente',
  'read.textSize': 'Tamaño',
  'read.settings': 'Ajustes de lectura',
  'read.foldAll': 'Plegar secciones',
  'read.unfoldAll': 'Desplegar secciones',
  'read.clearHighlights': 'Quitar resaltados',
  'note.meta': 'Datos de la nota',
  'note.noDomain': 'Sin dominio',
  'note.unassigned': 'Sin asignar',
  'note.untitled': 'Sin título',

  // ── Session extras ──────────────────────────────────────────────────────
  'session.question': 'Pregunta',
  'session.answer': 'Respuesta',
  'session.paused': 'Pausa de sesión',
  'session.ratingPlate': 'Valoración',
  'session.receipt.title': 'QUIPWA · Justificante de repaso',
  'session.receipt.cards': 'Fichas',
  'session.receipt.accuracy': 'Aciertos',
  'session.receipt.time': 'Tiempo',
  'session.receipt.due': 'Devolver {date}',
  'session.receipt.dueHint': 'Próximo repaso, según FSRS: {date}',
  'session.receipt.free': 'Repaso libre · FSRS sin cambios',
  'interval.today': 'hoy',
  'interval.days': 'd',
  'interval.weeks': ' sem',
  'interval.months': ' mes',
  'interval.years': ' a',
  'session.saveAndExit': 'Guardar progreso y salir',
  'session.accuracy': 'Precisión',
  'session.reviewed': 'Repasadas',

  // ── Errors / auth ───────────────────────────────────────────────────────
  'error.critical': '¡Algo salió mal!',
  'error.component': 'Error en componente',
  'error.retryHint': 'Haz clic en «Reintentar» para recargar el componente',
  'error.refreshHint': 'Refresca la página (Ctrl+R / Cmd+R)',
  'error.supportHint': 'Si el problema persiste, contacta al soporte',
  'auth.connectError': 'Error al conectar',
  'auth.connecting': 'Conectando con Google…',

  // ── Misc ────────────────────────────────────────────────────────────────
  'empty.noDecks': 'No hay mazos todavía',
  'empty.importFirst': 'Importar primer mazo',
  'tagPicker.search': 'Buscar o crear etiqueta…',
  'tagPicker.empty': 'Aún no hay etiquetas creadas',
  'tags.libraryTitle': 'Biblioteca de etiquetas',

  // ── Onboarding ──────────────────────────────────────────────────────────
  'onboarding.tagline': 'Notas y repaso espaciado en un solo flujo, guardado en tu Google Drive.',
  'onboarding.badges': '100% client-side · AGPL-3.0 · Sin servidor propio.',
  'onboarding.goToDecks': 'Ir a mis mazos',
  'onboarding.connect': 'Conectar Google',
  'onboarding.scopes': 'QUIPWA solo pide acceso a los archivos que crea, a su carpeta privada en Drive y a tu email. Google Tasks es opcional, desde Ajustes.',

  'onboarding.fsrs.title': 'Repasos a tu ritmo',
  'onboarding.fsrs.body': 'Algoritmo FSRS-4.5. Intervalos calculados por tu historial de respuestas, no fijos.',
  'onboarding.drive.title': 'Tu Drive',
  'onboarding.drive.body': 'Tus mazos, notas y progreso se guardan en tu propio Google Drive. QUIPWA no tiene servidor.',
  'onboarding.tasks.title': 'Google Tasks',
  'onboarding.tasks.body': 'Opcional: un recordatorio en Google Tasks con la fecha de tu próximo repaso.',
  'onboarding.theme.title': 'Personalización',
  'onboarding.theme.body': 'Temas, tipografía y atajos a tu gusto, con hasta 7 perfiles sincronizados en tu Drive.',
  'onboarding.offline.title': 'Offline',
  'onboarding.offline.body': 'Todo se guarda primero en tu dispositivo. Estudia y escribe sin conexión; se sincroniza al volver.',

  // ── Dashboard calendar strip ────────────────────────────────────────────
  'dashboard.calSyncing': 'Sincronizando Calendar…',
  'dashboard.noScheduled': 'Sin actividad programada',
  'dashboard.syncedAgo': 'Sincronizado {when}',
  'dashboard.neverSynced': 'Sin sincronizar todavía',
  'dashboard.upToDate': 'Al día',
  'dashboard.makeFlashcard': 'Crear flashcard desde esta nota',
  'dashboard.flashcard': 'Flashcard',

  // ── App shell / navigation ──────────────────────────────────────────────
  'nav.home': 'Inicio',
  'nav.decks': 'Mazos',
  'nav.tags': 'Etiquetas',
  'nav.notes': 'Notas',
  'nav.settings': 'Ajustes',
  'nav.appearance': 'Apariencia',
  /* Short form for the phone bottom bar, where six items share the width. */
  'nav.appearanceShort': 'Tema',
  'nav.sync': 'Sincronizar',
  'nav.syncing': 'Sincronizando…',
  'nav.syncTitle': 'Sincronizar con Google Drive',
  'nav.import': 'Importar mazo',
  'nav.logout': 'Cerrar sesión',

  'auth.loading': 'Cargando…',
  'auth.prompt': 'Conecta tu cuenta de Google para continuar',
  'auth.signIn': 'Iniciar sesión con Google',

  // ── Dashboard ───────────────────────────────────────────────────────────
  'dashboard.activeDecks': 'Mazos activos',
  'dashboard.recentNotes': 'Notas recientes',
  'dashboard.dueToday': 'Vencen hoy',
  'dashboard.noDecks': 'Aún no tienes mazos.',
  'dashboard.noNotes': 'Aún no tienes notas.',
  'dashboard.reviewNow': 'Repasar ahora',
  'dashboard.reviewNowCards': '{count} tarjetas',
  'dashboard.reviewNowCardsOne': '1 tarjeta',
  'dashboard.reviewNowDecks': '{count} mazos',
  'dashboard.reviewNowDecksOne': '1 mazo',
  'dashboard.reviewNowMinutes': '~{count} min',
  'dashboard.reviewNowHint': 'Todo lo pendiente de todos tus mazos, en una sesión',
  // The «Fichero» skin's display and drawers: short words for counters.
  'dashboard.lcdCards': 'fichas',
  'dashboard.lcdCard': 'ficha',
  'dashboard.lcdDecks': 'cajones',
  'dashboard.lcdDeck': 'cajón',
  'drawer.due': 'Pendientes',
  'drawer.new': 'Nuevas',
  'drawer.pull': 'Repasar',
  'dashboard.learnNew': 'Aprender nuevas',
  'dashboard.notStudied': 'Sin estudiar',
  'dashboard.pending': 'pendientes',
  'dashboard.cards': 'tarjetas',
  'dashboard.retention': '{value}% retención',
  'dashboard.allCaughtUp': '¡Todo al día! Sin tarjetas pendientes hoy.',
  'dashboard.nothingOverdue': 'Nada vencido.',
  'dashboard.overdue': 'Vencidos',

  'deck.pending': '{count} pendiente',
  'deck.pendingPlural': '{count} pendientes',
  'deck.cards': '{count} tarjetas',
  'deck.newCards': '{count} nuevas',
  'deck.today': 'hoy',
  'deck.yesterday': 'ayer',
  'deck.daysAgo': 'hace {n} d',

  // ── Decks ───────────────────────────────────────────────────────────────
  'decks.title': 'Biblioteca de mazos',
  'decks.count': '{count} mazos',
  'decks.searchPlaceholder': 'Buscar por nombre…',
  'decks.noResults': 'Sin resultados para «{query}»',
  'decks.import': 'Importar',
  'decks.empty': 'Todavía no hay mazos. Importa uno para empezar.',
  'decks.delete': 'Eliminar',
  'decks.deleting': 'Eliminando…',
  'decks.edit': 'Editar',
  'decks.freeStudy': 'Estudio libre',

  // ── Tags ────────────────────────────────────────────────────────────────
  'tags.title': 'Etiquetas',
  'tags.empty': 'Aún no hay etiquetas.',
  'tags.deckCount': '{count} mazos',
  'tags.noteCount': '{count} notas',
  'tags.cardCount': '{count} tarjetas',
  'tags.notesWithTag': 'Notas con esta etiqueta',
  'tags.back': 'Volver',
  'tags.emptyTitle': 'Sin etiquetas',
  'tags.emptyHint': 'Organiza tus mazos creando etiquetas temáticas.',
  'tags.createFirst': 'Crear mi primera etiqueta',
  'tags.new': 'Nueva etiqueta',
  'tags.namePlaceholder': 'Nombre de la etiqueta…',
  'tags.create': 'Crear',

  // ── Study session ───────────────────────────────────────────────────────
  'session.loading': 'Cargando tarjetas…',
  'session.emptyTitle': 'Sin tarjetas pendientes',
  'session.emptyDeck': 'Este mazo no tiene tarjetas para repasar.',
  'session.emptyMixed': 'No hay tarjetas pendientes en ningún mazo.',
  'session.emptyFree': 'Ninguno de los mazos seleccionados tiene tarjetas para repasar.',
  'session.emptyToday': 'No hay tarjetas pendientes para hoy en este mazo.',
  'session.backToDeck': 'Volver al mazo',
  'session.backToFree': 'Volver a Estudio libre',
  'session.exit': 'Salir',
  'session.deck': 'Mazo',
  'session.flip': 'Voltear tarjeta',
  'session.flipShort': 'Voltear',
  'session.currentDeck': 'Mazo actual',
  'session.orPress': 'o presiona',
  'session.rate': 'Calificar',

  // FSRS ratings. Kept short — they sit on buttons in a row.
  'rating.1': 'Nada',
  'rating.2': 'Difícil',
  'rating.3': 'Bien',
  'rating.4': 'Fácil',
  'rating.5': 'Perfecto',

  'session.done': 'Repaso terminado.',
  'session.greatWork': 'Excelente trabajo.',
  'session.progressSaved': 'Progreso guardado.',
  'session.taskScheduled': 'Tarea programada',
  'session.freeNoTask': 'Sesión libre — sin tarea programada',
  'session.taskFailed': 'No se pudo crear la tarea de Google Tasks',
  'session.freeNoFsrs': 'Repaso libre completado — FSRS sin cambios',
  'session.nextInTasks': 'Próxima sesión en tus Google Tasks',
  'session.noTaskOffline': 'Sin tarea (sin conexión)',
  'session.viewStats': 'Ver estadísticas',
  'session.backHome': 'Volver al inicio',
  'session.savedSyncing': 'Progreso guardado. Se está sincronizando con tu Google Drive.',
  'session.savedLocal': 'Progreso guardado en este dispositivo. Se subirá a tu Drive cuando inicies sesión.',
  'session.saving': 'Guardando progreso…',
  'session.freeExplain': 'El repaso libre no modifica el estado FSRS. Úsalo para repasar antes de exámenes.',

  // ── Free study ──────────────────────────────────────────────────────────
  'freeStudy.title': 'Estudio libre',
  'freeStudy.decksToInclude': 'Mazos a incluir',
  'freeStudy.cardLimit': 'Límite de tarjetas',
  'freeStudy.percentPerDeck': 'Porcentaje de cada mazo',
  'freeStudy.totalCards': 'Total de tarjetas a repasar',
  'freeStudy.totalNumber': 'Número total',
  'freeStudy.noLimit': 'Sin límite',
  'freeStudy.percentPerDeckShort': '% por mazo',
  'freeStudy.subtitle': 'Selecciona mazos y configura tu sesión personalizada',
  'freeStudy.available': '{decks} mazos · {cards} tarjetas disponibles',
  'freeStudy.estimatedCards': 'tarjetas estimadas',
  'freeStudy.decks': 'mazos',
  'freeStudy.estimated': 'estimado',
  'freeStudy.start': 'Iniciar Estudio libre',

  ...SYSTEM_ES,
  ...SETTINGS_ES,
  ...APPEARANCE_ES,
  ...STARTER_ES,
  ...DECK_EDITOR_ES,
  ...SEARCH_ES,
  ...EXAM_ES,
  ...VOICE_ES,
  ...INTEGRITY_ES,
} as const;

/** Every locale must define exactly the keys `es` defines. */
export type TranslationKey = keyof typeof es;
type Catalog = Record<TranslationKey, string>;

const en: Catalog = {
  'noteType.Quick Note': 'Quick note',
  'noteType.Book': 'Book',
  'noteType.Short Course': 'Short course',
  'noteType.Long Course': 'Long course',

  'noteStatus.Pending': 'Pending',
  'noteStatus.In Progress': 'In progress',
  'noteStatus.Completed': 'Completed',
  'noteStatus.Mastered': 'Mastered',

  'common.all': 'All',
  'common.none': '—',
  'common.clear': 'Clear',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.sure': 'Sure?',
  'key.space': 'Space',
  'key.escape': 'Esc',

  'notes.title': 'Knowledge base',
  'notes.count': '{count} note(s)',
  'notes.searchPlaceholder': 'Search notes, domains, tags…',
  'notes.filters': 'Filters',
  'notes.import': 'Import',
  'notes.newNote': 'New note',

  'notes.upgradeToLongCourse': 'Convert to Long course',
  'notes.promoting': 'converting…',

  'notes.col.title': 'Title',
  'notes.col.type': 'Type',
  'notes.col.status': 'Status',
  'notes.col.domain': 'Domain',
  'notes.col.tags': 'Tags',
  'notes.col.updated': 'Updated',

  'notes.filter.type': 'Type',
  'notes.filter.status': 'Status',
  'notes.filter.domain': 'Domain',
  'notes.filter.clearAll': 'Clear filters',
  'notes.filter.noDomains': 'No domains yet',

  'notes.empty': 'No notes match your search.',
  'notes.emptyFiltered': 'No notes match these filters.',
  'notes.delete': 'Delete note',
  'notes.deleteAria': 'Delete {title}',

  'notes.select': 'Select {title}',
  'notes.selectAll': 'Select all',
  'notes.selectedCount': '{count} selected',
  'notes.clearSelection': 'Clear selection',
  'notes.deleteSelected': 'Delete',

  'notes.deleteModal.titleOne': 'Delete this note?',
  'notes.deleteModal.titleMany': 'Delete {count} notes?',
  'notes.deleteModal.warning': 'They will be removed from Google Drive. This cannot be undone.',
  'notes.deleteModal.courseWarning':
    'Includes {count} long course(s): their modules will be deleted too ({modules} in total).',
  'notes.deleteModal.andMore': '…and {count} more',
  'notes.deleteModal.confirm': 'Yes, delete',
  'notes.deleteModal.deleting': 'Deleting…',
  'notes.deleteModal.failed': 'Could not delete {count} note(s). They are still in Drive.',

  'notes.footer.count': '{shown} of {total} notes',
  'notes.footer.sortedBy': 'Sorted by {field}',

  'notes.time.justNow': 'Just now',
  'notes.time.hoursAgo': '{n}h ago',
  'notes.time.daysAgo': '{n}d ago',

  'createNote.title': 'New note',
  'createNote.subtitle': 'Pick a type to insert the matching template',
  'createNote.titlePlaceholder': 'Note title…',
  'createNote.domainPlaceholder': 'e.g. Mathematics',
  'createNote.tagsPlaceholder': 'Comma-separated',
  'createNote.template': 'Template',
  'createNote.status': 'Status',
  'createNote.domain': 'Domain',
  'createNote.tags': 'Tags',
  'createNote.creating': 'Creating…',
  'createNote.create': 'Create note',
  'noteType.Quick Note.desc': 'Free capture, no structure',
  'noteType.Book.desc': 'Reading with notes and quotes',
  'noteType.Short Course.desc': 'Structured learning',
  'noteType.Long Course.desc': 'Multiple in-depth modules',

  'importNotes.title': 'Import notes',
  'importNotes.hint': 'Loose .md files or a whole folder (vault)',
  'importNotes.pickFiles': 'Choose files',
  'importNotes.pickFilesHint': 'One or more .md',
  'importNotes.pickFolder': 'Choose folder',
  'importNotes.pickFolderHint': 'Imports every .md inside',
  'importNotes.unreadable': 'Could not be read',
  'importNotes.imported': '{count} notes imported',
  'importNotes.failed': '{count} failed:',
  'importNotes.importing': 'Importing… {progress}%',
  'importNotes.submit': 'Import {count}',
  'importNotes.summary': '{count} notes ready to import',

  'tagDetail.loading': 'Loading decks…',
  'tagDetail.notFound': 'Tag not found',
  'tagDetail.goToLibrary': 'Go to library',
  'tagDetail.empty': 'No decks or notes with this tag yet.',
  'tagDetail.edit': 'Edit tag',
  'tagDetail.name': 'Name',
  'tagDetail.namePlaceholder': 'e.g. biology',
  'tagDetail.color': 'Colour',
  'tagDetail.save': 'Save changes',
  'tagDetail.saving': 'Saving…',
  'tagDetail.confirm': 'Confirm',

  'conflict.bannerOne': '{count} change was resolved automatically while syncing',
  'conflict.bannerMany': '{count} changes were resolved automatically while syncing',
  'conflict.review': 'Review',
  'conflict.title': 'Conflicting changes',
  'conflict.subtitle': 'The same thing was edited on two devices. One version is already applied — pick the other if you prefer it.',
  'conflict.thisDevice': 'This device',
  'conflict.otherDevice': 'Drive (other device)',
  'conflict.applied': 'Applied',
  'conflict.keepThis': 'Keep this',
  'conflict.useThis': 'Use this',
  'conflict.noData': 'No data',
  'conflict.note': 'Note',
  'conflict.reviewProgress': 'Review progress',
  'conflict.detected': 'Detected {when}',
  'conflict.unavailable': 'That version is not available',
  'conflict.dbNotReady': 'The database is not ready',
  'conflict.notesDbNotReady': 'The notes database is not ready',
  'conflict.applyFailed': 'Could not apply that version',
  'conflict.characters': '{count} characters',
  'conflict.reviewsAndLapses': '{reps} reviews · {lapses} lapses',
  'conflict.neverReviewed': 'never reviewed',

  'time.justNow': 'just now',
  'time.minutesAgo': '{n} min ago',
  'time.hoursAgo': '{n}h ago',
  'time.daysAgo': '{n}d ago',

  'palette.placeholder': 'Search a deck or command…',
  'palette.noResults': 'No results found',
  'palette.enterToSelect': 'ENTER to select',
  'palette.toNavigate': 'to navigate',
  'palette.navigation': 'Navigation',
  'palette.goHome': 'Go to Home',
  'palette.toggleLight': 'Toggle light mode',
  'palette.toggleSkin': 'Switch skin (Flat / Fichero)',
  'palette.soundOn': 'Turn on Fichero sound',
  'palette.soundOff': 'Turn off Fichero sound',
  'palette.goNotes': 'Go to Notes',
  'palette.importDeck': 'Import new deck',
  'palette.accountSettings': 'Account settings',

  'importDeck.title': 'Import deck',
  'importDeck.drop': 'Drop your file here',
  'importDeck.formats': 'CSV · .wflash',
  'importDeck.badFormat': 'Unsupported format. Use .csv or .wflash',
  'importDeck.noCards': 'No valid cards found',
  'importDeck.deckName': 'Deck name',
  'importDeck.deckNamePlaceholder': 'e.g. Computer Architecture',
  'importDeck.newDeck': 'New deck',
  'importDeck.preview': 'Preview',
  'importDeck.sample': 'Sample',
  'importDeck.pressEnter': 'Press Enter or the button to continue',
  'importDeck.confirm': 'Confirm import',
  'importDeck.importing': 'Importing…',
  'importDeck.done': 'Done',
  'importDeck.ready': 'All set!',
  'importDeck.importAnother': 'Import another',

  'deckDetail.notFound': 'Deck not found',
  'deckDetail.library': 'Library',
  'deckDetail.upcoming': 'Upcoming events',
  'deckDetail.noTasks': 'No tasks scheduled in Google Tasks',
  'deckDetail.anatomy': 'Deck anatomy',
  'deckDetail.retention': 'Retention',
  'deckDetail.visualCustom': 'Visual customisation',
  'deckDetail.accentColor': 'Accent colour',
  'deckDetail.bgTexture': 'Background texture',
  'deckDetail.computingStats': 'Computing stats…',
  'deckDetail.history': 'Progress history',
  'deckDetail.recentSessions': 'Recent sessions',
  'deckDetail.noSessions': 'No sessions recorded yet',
  'deckDetail.today': 'Today',
  'deckDetail.oldest': 'Oldest',
  'texture.minimal': 'Minimal (no texture)',
  'texture.dotted': 'Dotted grid',
  'texture.ruled': 'Ruled paper',
  'texture.grid': 'Grid',

  'cardEditor.selectCard': 'Select a card to edit',
  'cardEditor.front': 'Question / Front',
  'cardEditor.back': 'Answer / Back',
  'cardEditor.frontPlaceholder': 'Write the question…',
  'cardEditor.backPlaceholder': 'Write the answer. You can use $LaTeX$ for formulas.',
  'cardEditor.search': 'Search…',
  'cardEditor.noResults': 'No results',
  'cardEditor.preview': 'Preview',
  'cardEditor.question': 'Question',
  'cardEditor.answer': 'Answer',

  'noteEditor.loading': 'Loading note',
  'noteEditor.downloading': 'Downloading from Google Drive…',
  'noteEditor.loadError': 'Failed to load',
  'noteEditor.loadingImage': 'Loading image…',
  'noteEditor.loadingNote': 'Loading…',
  'noteEditor.noteLoadFailed': 'Could not load the note.',
  'noteEditor.mathPlaceholder': 'LaTeX — e.g. E = mc^2',
  'noteEditor.mathHint': 'Enter to save · Esc to cancel',
  'noteEditor.noNotesFound': 'No notes found',
  'noteEditor.noResult': 'No results',
  'noteEditor.togglePlaceholder': 'Toggle',

  'flashcard.title': 'Create flashcard',
  'flashcard.targetDeck': 'Target deck',
  'flashcard.deckPlaceholder': 'e.g. Algebra — Cards',
  'flashcard.front': 'Question (front)',
  'flashcard.back': 'Answer (back)',
  'flashcard.backPlaceholder': 'Write the answer…',

  'read.find': 'Search in note…',
  'read.replace': 'Replace with…',
  'read.outline': 'Outline',
  'read.lineHeight': 'Line height',
  'read.font': 'Font',
  'read.textSize': 'Size',
  'read.settings': 'Reading settings',
  'read.foldAll': 'Fold sections',
  'read.unfoldAll': 'Unfold sections',
  'read.clearHighlights': 'Clear highlights',
  'note.meta': 'Note details',
  'note.noDomain': 'No domain',
  'note.unassigned': 'Unassigned',
  'note.untitled': 'Untitled',

  'session.question': 'Question',
  'session.answer': 'Answer',
  'session.paused': 'Session paused',
  'session.ratingPlate': 'Rating',
  'session.receipt.title': 'QUIPWA · Review receipt',
  'session.receipt.cards': 'Cards',
  'session.receipt.accuracy': 'Correct',
  'session.receipt.time': 'Time',
  'session.receipt.due': 'Due back {date}',
  'session.receipt.dueHint': 'Next review, as FSRS scheduled it: {date}',
  'session.receipt.free': 'Free review · FSRS unchanged',
  'interval.today': 'today',
  'interval.days': 'd',
  'interval.weeks': ' wk',
  'interval.months': ' mo',
  'interval.years': ' y',
  'session.saveAndExit': 'Save progress and exit',
  'session.accuracy': 'Accuracy',
  'session.reviewed': 'Reviewed',

  'error.critical': 'Something went wrong!',
  'error.component': 'Component error',
  'error.retryHint': 'Click “Retry” to reload the component',
  'error.refreshHint': 'Refresh the page (Ctrl+R / Cmd+R)',
  'error.supportHint': 'If the problem persists, contact support',
  'auth.connectError': 'Connection failed',
  'auth.connecting': 'Connecting to Google…',

  'empty.noDecks': 'No decks yet',
  'empty.importFirst': 'Import your first deck',
  'tagPicker.search': 'Search or create a tag…',
  'tagPicker.empty': 'No tags created yet',
  'tags.libraryTitle': 'Tag library',

  'onboarding.tagline': 'Notes and spaced repetition in one flow, saved to your Google Drive.',
  'onboarding.badges': '100% client-side · AGPL-3.0 · No server of our own.',
  'onboarding.goToDecks': 'Go to my decks',
  'onboarding.connect': 'Connect Google',
  'onboarding.scopes': 'QUIPWA only asks for the files it creates, its private folder in Drive and your email. Google Tasks is optional, from Settings.',

  'onboarding.fsrs.title': 'Reviews at your pace',
  'onboarding.fsrs.body': 'FSRS-4.5 algorithm. Intervals computed from your answer history, not fixed.',
  'onboarding.drive.title': 'Your Drive',
  'onboarding.drive.body': 'Your decks, notes and progress are saved to your own Google Drive. QUIPWA has no server.',
  'onboarding.tasks.title': 'Google Tasks',
  'onboarding.tasks.body': 'Optional: a Google Tasks reminder with the date of your next review.',
  'onboarding.theme.title': 'Personalisation',
  'onboarding.theme.body': 'Themes, typography and shortcuts your way, with up to 7 profiles synced to your Drive.',
  'onboarding.offline.title': 'Offline',
  'onboarding.offline.body': 'Everything is saved on your device first. Study and write offline; it syncs when you are back.',

  'dashboard.calSyncing': 'Syncing Calendar…',
  'dashboard.noScheduled': 'Nothing scheduled',
  'dashboard.syncedAgo': 'Synced {when}',
  'dashboard.neverSynced': 'Not synced yet',
  'dashboard.upToDate': 'Up to date',
  'dashboard.makeFlashcard': 'Create a flashcard from this note',
  'dashboard.flashcard': 'Flashcard',

  'nav.home': 'Home',
  'nav.decks': 'Decks',
  'nav.tags': 'Tags',
  'nav.notes': 'Notes',
  'nav.settings': 'Settings',
  'nav.appearance': 'Appearance',
  'nav.appearanceShort': 'Theme',
  'nav.sync': 'Sync',
  'nav.syncing': 'Syncing…',
  'nav.syncTitle': 'Sync with Google Drive',
  'nav.import': 'Import deck',
  'nav.logout': 'Sign out',

  'auth.loading': 'Loading…',
  'auth.prompt': 'Connect your Google account to continue',
  'auth.signIn': 'Sign in with Google',

  'dashboard.activeDecks': 'Active decks',
  'dashboard.recentNotes': 'Recent notes',
  'dashboard.dueToday': 'Due today',
  'dashboard.noDecks': 'No decks yet.',
  'dashboard.noNotes': 'No notes yet.',
  'dashboard.reviewNow': 'Review now',
  'dashboard.reviewNowCards': '{count} cards',
  'dashboard.reviewNowCardsOne': '1 card',
  'dashboard.reviewNowDecks': '{count} decks',
  'dashboard.reviewNowDecksOne': '1 deck',
  'dashboard.reviewNowMinutes': '~{count} min',
  'dashboard.reviewNowHint': 'Everything due in all your decks, in one session',
  'dashboard.lcdCards': 'cards',
  'dashboard.lcdCard': 'card',
  'dashboard.lcdDecks': 'drawers',
  'dashboard.lcdDeck': 'drawer',
  'drawer.due': 'Due',
  'drawer.new': 'New',
  'drawer.pull': 'Review',
  'dashboard.learnNew': 'Learn new',
  'dashboard.notStudied': 'Not studied',
  'dashboard.pending': 'pending',
  'dashboard.cards': 'cards',
  'dashboard.retention': '{value}% retention',
  'dashboard.allCaughtUp': 'All caught up! No cards due today.',
  'dashboard.nothingOverdue': 'Nothing overdue.',
  'dashboard.overdue': 'Overdue',

  'deck.pending': '{count} due',
  'deck.pendingPlural': '{count} due',
  'deck.cards': '{count} cards',
  'deck.newCards': '{count} new',
  'deck.today': 'today',
  'deck.yesterday': 'yesterday',
  'deck.daysAgo': '{n}d ago',

  'decks.title': 'Deck library',
  'decks.count': '{count} decks',
  'decks.searchPlaceholder': 'Search by name…',
  'decks.noResults': 'No results for “{query}”',
  'decks.import': 'Import',
  'decks.empty': 'No decks yet. Import one to get started.',
  'decks.delete': 'Delete',
  'decks.deleting': 'Deleting…',
  'decks.edit': 'Edit',
  'decks.freeStudy': 'Free study',

  'tags.title': 'Tags',
  'tags.empty': 'No tags yet.',
  'tags.deckCount': '{count} decks',
  'tags.noteCount': '{count} notes',
  'tags.cardCount': '{count} cards',
  'tags.notesWithTag': 'Notes with this tag',
  'tags.back': 'Back',
  'tags.emptyTitle': 'No tags',
  'tags.emptyHint': 'Organise your decks by creating thematic tags.',
  'tags.createFirst': 'Create my first tag',
  'tags.new': 'New tag',
  'tags.namePlaceholder': 'Tag name…',
  'tags.create': 'Create',

  'session.loading': 'Loading cards…',
  'session.emptyTitle': 'No cards due',
  'session.emptyDeck': 'This deck has no cards to review.',
  'session.emptyMixed': 'No cards are due in any deck.',
  'session.emptyFree': 'None of the selected decks has cards to review.',
  'session.emptyToday': 'No cards due today in this deck.',
  'session.backToDeck': 'Back to deck',
  'session.backToFree': 'Back to Free study',
  'session.exit': 'Exit',
  'session.deck': 'Deck',
  'session.flip': 'Flip card',
  'session.flipShort': 'Flip',
  'session.currentDeck': 'Current deck',
  'session.orPress': 'or press',
  'session.rate': 'Rate',

  'rating.1': 'Again',
  'rating.2': 'Hard',
  'rating.3': 'Good',
  'rating.4': 'Easy',
  'rating.5': 'Perfect',

  'session.done': 'Review complete.',
  'session.greatWork': 'Great work.',
  'session.progressSaved': 'Progress saved.',
  'session.taskScheduled': 'Task scheduled',
  'session.freeNoTask': 'Free session — no task scheduled',
  'session.taskFailed': 'Could not create the Google Tasks entry',
  'session.freeNoFsrs': 'Free review complete — FSRS unchanged',
  'session.nextInTasks': 'Next session in your Google Tasks',
  'session.noTaskOffline': 'No task (offline)',
  'session.viewStats': 'View stats',
  'session.backHome': 'Back to home',
  'session.savedSyncing': 'Progress saved. Syncing with your Google Drive.',
  'session.savedLocal': 'Progress saved on this device. It will upload to your Drive once you sign in.',
  'session.saving': 'Saving progress…',
  'session.freeExplain': 'Free review does not change FSRS state. Use it to brush up before exams.',

  'freeStudy.title': 'Free study',
  'freeStudy.decksToInclude': 'Decks to include',
  'freeStudy.cardLimit': 'Card limit',
  'freeStudy.percentPerDeck': 'Percentage of each deck',
  'freeStudy.totalCards': 'Total cards to review',
  'freeStudy.totalNumber': 'Total number',
  'freeStudy.noLimit': 'No limit',
  'freeStudy.percentPerDeckShort': '% per deck',
  'freeStudy.subtitle': 'Pick decks and configure your custom session',
  'freeStudy.available': '{decks} decks · {cards} cards available',
  'freeStudy.estimatedCards': 'estimated cards',
  'freeStudy.decks': 'decks',
  'freeStudy.estimated': 'estimated',
  'freeStudy.start': 'Start free study',

  ...SYSTEM_EN,
  ...SETTINGS_EN,
  ...APPEARANCE_EN,
  ...STARTER_EN,
  ...DECK_EDITOR_EN,
  ...SEARCH_EN,
  ...EXAM_EN,
  ...VOICE_EN,
  ...INTEGRITY_EN,
};

export const CATALOGS: Record<Locale, Catalog> = { es, en };

/** Fill `{name}` placeholders. Unknown placeholders are left untouched so a
 *  typo shows up as `{name}` in the UI rather than silently vanishing. */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match
  );
}
